import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { CombinedContext } from "../../context/combinedContext";
import translations from "../../translations/translations";
import usersRequest from "../../request/usersRequest";
import BannerMessageFixed from "../common/bannerMessageFixed/bannerMessageFixed";
import PasswordField from "../common/passwordField/passwordField";
import PasswordStrength from "../common/passwordStrength/passwordStrength";
import { Check, X } from "lucide-react";
import "./forgotPasswordConfirm.css";

const ConfirmForgotPassword = () => {
    const { language } = useContext(CombinedContext);
    const navigate = useNavigate();

    const [email, setEmail]                     = useState("");
    const [code, setCode]                       = useState("");
    const [password, setPassword]               = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading]                 = useState(false);
    const [success, setSuccess]                 = useState(false);
    const [banner, setBanner]                   = useState({ text: "", type: "info", exiting: false });
    const [invalidFields, setInvalidFields]     = useState(new Set());

    /* The banner says what's wrong; these say where. A flagged field clears
       as soon as it's touched, so the red only ever marks what's still open. */
    const clearInvalid = (field) => {
        setInvalidFields((prev) => {
            if (!prev.has(field)) return prev;
            const next = new Set(prev);
            next.delete(field);
            return next;
        });
    };

    const invalidClass = (field) => (invalidFields.has(field) ? " is-invalid" : "");

    function isStrongPassword(p) {
        return (
            typeof p === "string" &&
            p.length >= 8 &&
            /[A-Z]/.test(p) &&
            /[a-z]/.test(p) &&
            /[0-9]/.test(p) &&
            /[\^$*.\[\]{}()?"!@#%&/\\,><':;|_~`+=-]/.test(p)
        );
    }

    const showBanner = (text, type = "error") => {
        setBanner({ text, type, exiting: false });
        setTimeout(() => setBanner((prev) => ({ ...prev, exiting: true })), 2500);
        setTimeout(() => setBanner({ text: "", type: "info", exiting: false }), 2750);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const missing = new Set();
        if (!email) missing.add("email");
        if (!code) missing.add("code");
        if (!password) missing.add("password");
        if (!confirmPassword) missing.add("confirmPassword");

        if (missing.size > 0) {
            setInvalidFields(missing);
            showBanner(translations[language].confirmForgotPassword.toast.required);
            return;
        }
        if (!isStrongPassword(password)) {
            setInvalidFields(new Set(["password"]));
            showBanner(translations[language].confirmForgotPassword.toast.weakPassword);
            return;
        }
        if (password !== confirmPassword) {
            setInvalidFields(new Set(["confirmPassword"]));
            showBanner(translations[language].confirmForgotPassword.toast.mismatch);
            return;
        }

        setInvalidFields(new Set());

        try {
            setLoading(true);
            await usersRequest.confirmForgotPassword({ email, code, newPassword: password });
            setSuccess(true);
            setTimeout(() => navigate("/signin"), 1500);
        } catch (err) {
            console.error(err);
            showBanner(translations[language].confirmForgotPassword.toast.invalidCode);
        } finally {
            setLoading(false);
        }
    };

    const t = translations[language].confirmForgotPassword;

    return (
        <div className="sf-auth-page sf-cfp-root">
            <div className="sf-auth-shell">
                <div className="sf-auth-card">
                    <div className="sf-auth-head">
                        <h1 className="sf-auth-title">{t.title}</h1>
                        <p className="sf-auth-sub">{t.subtitle}</p>
                    </div>

                    {success ? (
                        <div className="sf-cfp-success">
                            <span className="sf-cfp-success-icon">
                                <Check size={24} strokeWidth={3} />
                            </span>
                            <p>{t.success}</p>
                        </div>
                    ) : (
                        <>
                            <div className="sf-auth-field">
                                <label className="sf-auth-label">{t.email}</label>
                                <input type="email" className={`sf-auth-input${invalidClass("email")}`}
                                    placeholder={t.email}
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        clearInvalid("email");
                                    }}
                                    autoComplete="email" />
                            </div>

                            <div className="sf-auth-field">
                                <label className="sf-auth-label">{t.code}</label>
                                <input type="text" className={`sf-auth-input sf-cfp-code${invalidClass("code")}`}
                                    placeholder="••••••"
                                    value={code}
                                    onChange={(e) => {
                                        setCode(e.target.value);
                                        clearInvalid("code");
                                    }}
                                    autoComplete="one-time-code" inputMode="numeric" maxLength={10} />
                            </div>

                            <div className="sf-auth-field">
                                <label className="sf-auth-label">{t.password}</label>
                                <PasswordField value={password}
                                    onChange={(value) => {
                                        setPassword(value);
                                        clearInvalid("password");
                                    }}
                                    placeholder={t.password}
                                    autoComplete="new-password"
                                    className={invalidClass("password").trim()} />
                                <PasswordStrength value={password} />
                            </div>

                            <div className="sf-auth-field">
                                <label className="sf-auth-label">{t.confirmPassword}</label>
                                <PasswordField value={confirmPassword}
                                    onChange={(value) => {
                                        setConfirmPassword(value);
                                        clearInvalid("confirmPassword");
                                    }}
                                    placeholder={t.confirmPassword}
                                    autoComplete="new-password"
                                    className={invalidClass("confirmPassword").trim()} />
                                {confirmPassword && (
                                    <p className={`sf-auth-match ${password === confirmPassword ? "is-ok" : "is-bad"}`} aria-live="polite">
                                        {password === confirmPassword ? <Check size={13} strokeWidth={3} /> : <X size={13} strokeWidth={3} />}
                                        {password === confirmPassword
                                            ? translations[language].register.match.ok
                                            : translations[language].register.match.bad}
                                    </p>
                                )}
                            </div>

                            <button className="sf-auth-cta" onClick={handleSubmit} disabled={loading}>
                                {loading && <span className="sf-auth-spinner" aria-hidden="true" />}
                                {loading ? t.submitting : t.submit}
                            </button>
                        </>
                    )}

                    <BannerMessageFixed text={banner.text} type={banner.type} exiting={banner.exiting} />
                </div>
            </div>
        </div>
    );
};

export default ConfirmForgotPassword;

// import { useState, useContext } from "react";
// import { useNavigate } from "react-router-dom";
// import { CombinedContext } from "../../context/combinedContext";
// import translations from "../../translations/translations";
// import usersRequest from "../../request/usersRequest";
// import BannerMessageFixed from "../common/bannerMessageFixed/bannerMessageFixed";
// import PasswordField from "../common/passwordField/passwordField";
// import "./forgotPasswordConfirm.css";

// const ConfirmForgotPassword = () => {
// 	const { language } = useContext(CombinedContext);
// 	const navigate = useNavigate();

// 	const [email, setEmail] = useState("");
// 	const [code, setCode] = useState("");
// 	const [password, setPassword] = useState("");
// 	const [confirmPassword, setConfirmPassword] = useState("");
// 	const [loading, setLoading] = useState(false);
// 	const [success, setSuccess] = useState(false);
// 	const [banner, setBanner] = useState({ text: "", type: "info", exiting: false });

// 	function isStrongPassword(p) {
// 		return (
// 			typeof p === "string" &&
// 			p.length >= 8 &&
// 			/[A-Z]/.test(p) &&
// 			/[a-z]/.test(p) &&
// 			/[0-9]/.test(p) &&
// 			/[\^$*.\[\]{}()?"!@#%&/\\,><':;|_~`+=-]/.test(p)
// 		);
// 	}

// 	const showBanner = (text, type = "error") => {
// 		setBanner({ text, type, exiting: false });
// 		setTimeout(() => setBanner((prev) => ({ ...prev, exiting: true })), 2500);
// 		setTimeout(() => setBanner({ text: "", type: "info", exiting: false }), 2750);
// 	};

// 	const handleSubmit = async (e) => {
// 		e.preventDefault();

// 		if (!email || !code || !password || !confirmPassword) {
// 			showBanner(translations[language].confirmForgotPassword.toast.required);
// 			return;
// 		}

// 		if (!isStrongPassword(password)) {
// 			showBanner(translations[language].confirmForgotPassword.toast.weakPassword);
// 			return;
// 		}

// 		if (password !== confirmPassword) {
// 			showBanner(translations[language].confirmForgotPassword.toast.mismatch);
// 			return;
// 		}

// 		try {
// 			setLoading(true);
// 			await usersRequest.confirmForgotPassword({ email, code, newPassword: password });
// 			setSuccess(true);
// 			setTimeout(() => navigate("/signin"), 1500);
// 		} catch (err) {
// 			console.error(err);
// 			showBanner(translations[language].confirmForgotPassword.toast.invalidCode);
// 		} finally {
// 			setLoading(false);
// 		}
// 	};

// 	return (
// 		<div className="container">
// 			<div className="card" style={{ maxWidth: 400, margin: "40px auto" }}>
// 				<div className="hd">
// 					<div>{translations[language].confirmForgotPassword.title}</div>
// 				</div>
// 				<div className="bd">
// 					{success ? (
// 						<p className="sf-confirmfp-success">
// 							{translations[language].confirmForgotPassword.success}
// 						</p>
// 					) : (
// 						<>
// 							<p className="sf-confirmfp-subtitle">
// 								{translations[language].confirmForgotPassword.subtitle}
// 							</p>
// 							<div className="row">
// 								<input
// 									type="email"
// 									className="input"
// 									placeholder={translations[language].confirmForgotPassword.email}
// 									value={email}
// 									onChange={(e) => setEmail(e.target.value)}
// 								/>
// 								<input
// 									type="text"
// 									className="input"
// 									placeholder={translations[language].confirmForgotPassword.code}
// 									value={code}
// 									onChange={(e) => setCode(e.target.value)}
// 								/>
// 								<PasswordField
// 									value={password}
// 									onChange={setPassword}
// 									placeholder={translations[language].confirmForgotPassword.password}
// 									autoComplete="new-password"
// 								/>
// 								<PasswordField
// 									value={confirmPassword}
// 									onChange={setConfirmPassword}
// 									placeholder={translations[language].confirmForgotPassword.confirmPassword}
// 									autoComplete="new-password"
// 								/>
// 							</div>
// 							<button
// 								className="btn brand"
// 								style={{ width: "100%", marginTop: 10 }}
// 								onClick={handleSubmit}
// 								disabled={loading}
// 							>
// 								{loading
// 									? translations[language].confirmForgotPassword.submitting
// 									: translations[language].confirmForgotPassword.submit}
// 							</button>
// 						</>
// 					)}

// 					<BannerMessageFixed
// 						text={banner.text}
// 						type={banner.type}
// 						exiting={banner.exiting}
// 					/>
// 				</div>
// 			</div>
// 		</div>
// 	);
// };

// export default ConfirmForgotPassword;